import { useEffect, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import { Activity, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Gauge, Loader2, Radio, Thermometer, Wifi, WifiOff, X, Zap } from 'lucide-react';
import { fetchSmartloggerCurrent, fetchSmartloggerHistory } from '@/api/smartlogger';
import type { SmartloggerCurrentRow, SmartloggerHistoryRow } from '@/api/smartlogger';
import { useErrorToast } from '@/lib/notify';

function dateString(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function moveDate(value: string, days: number): string {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return dateString(date);
}

function MetricIcon({ tag }: { tag: string | null }) {
  if (tag?.includes('voltage')) return <Gauge size={15} />;
  if (tag?.includes('current')) return <Activity size={15} />;
  if (tag?.includes('frequency')) return <Radio size={15} />;
  if (tag?.includes('temperature')) return <Thermometer size={15} />;
  return <Zap size={15} />;
}

function HighchartsLine({ rows, unit }: { rows: SmartloggerHistoryRow[]; unit: string | null }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = Highcharts.chart(ref.current, {
      chart: { type: 'line', height: 340, backgroundColor: 'transparent' },
      title: { text: '' },
      credits: { enabled: false },
      xAxis: { type: 'datetime', title: { text: 'Saat' } },
      yAxis: { title: { text: unit || 'Değer' } },
      tooltip: { valueDecimals: 2, valueSuffix: unit ? ` ${unit}` : '' },
      legend: { enabled: false },
      series: [{
        type: 'line',
        name: 'Değer',
        color: '#2563eb',
        data: rows.map((row) => [new Date(row.timestamp).getTime(), Number(row.valueNumeric)]),
      }],
    });
    return () => chart.destroy();
  }, [rows, unit]);
  return <div ref={ref} className="w-full" style={{ height: '340px' }} />;
}

function ThumbnailChart({ rows }: { rows: SmartloggerHistoryRow[] }) {
  const values = rows.map((row) => Number(row.valueNumeric)).filter((value) => Number.isFinite(value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const points = values.map((value, index) => {
    const x = values.length === 1 ? 120 : (index / (values.length - 1)) * 232 + 4;
    const y = 68 - ((value - min) / range) * 60;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg viewBox="0 0 240 76" preserveAspectRatio="none" className="h-[76px] w-full" aria-hidden="true">
      <polyline points={points} fill="none" stroke="#2563eb" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
    </svg>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Bilinmeyen hata';
}

export default function SmartloggerPanel({ plantUuid, defaultOpen = false, collapsible = true }: { plantUuid: string; defaultOpen?: boolean; collapsible?: boolean }) {
  const [open, setOpen] = useState(defaultOpen || !collapsible);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useErrorToast(error);
  const [rows, setRows] = useState<SmartloggerCurrentRow[]>([]);
  const [selected, setSelected] = useState<SmartloggerCurrentRow | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyDate, setHistoryDate] = useState(dateString(new Date()));
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState<string | null>(null);
  useErrorToast(historyError);
  const [historyRows, setHistoryRows] = useState<SmartloggerHistoryRow[]>([]);
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [thumbnailRows, setThumbnailRows] = useState<SmartloggerHistoryRow[]>([]);
  const [thumbnailLoading, setThumbnailLoading] = useState(false);
  const currentLoaded = useRef(false);
  const historyCache = useRef(new Map<string, SmartloggerHistoryRow[]>());

  useEffect(() => {
    if (!open || currentLoaded.current) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void fetchSmartloggerCurrent(plantUuid)
      .then((response) => {
        if (!cancelled) {
          currentLoaded.current = true;
          setRows(response.rows || []);
        }
      })
      .catch((reason) => {
        if (!cancelled) setError(`Smartlogger verileri yüklenemedi: ${errorMessage(reason)}`);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, plantUuid]);

  useEffect(() => {
    if (!selected) return;
    const key = `${plantUuid}:${selected.id}:${historyDate}`;
    const cached = historyCache.current.get(key);
    if (cached) {
      setHistoryLoading(false);
      setHistoryRows(cached);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    setHistoryError(null);
    void fetchSmartloggerHistory(plantUuid, { registerMapId: selected.id, date: historyDate })
      .then((response) => {
        if (!cancelled) {
          historyCache.current.set(key, response.rows || []);
          setHistoryRows(response.rows || []);
        }
      })
      .catch((reason) => {
        if (!cancelled) {
          setHistoryRows([]);
          setHistoryError(`Geçmiş verisi yüklenemedi: ${errorMessage(reason)}`);
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => { cancelled = true; };
  }, [historyDate, plantUuid, selected]);

  useEffect(() => {
    if (hoveredId === null) {
      setThumbnailLoading(false);
      return;
    }
    const row = rows.find((item) => item.id === hoveredId);
    if (!row) return;
    const today = dateString(new Date());
    const key = `${plantUuid}:${row.id}:${today}`;
    const cached = historyCache.current.get(key);
    if (cached) {
      setThumbnailLoading(false);
      setThumbnailRows(cached);
      return;
    }
    let cancelled = false;
    setThumbnailRows([]);
    setThumbnailLoading(true);
    void fetchSmartloggerHistory(plantUuid, { registerMapId: row.id, date: today })
      .then((response) => {
        if (!cancelled) {
          historyCache.current.set(key, response.rows || []);
          setThumbnailRows(response.rows || []);
        }
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setThumbnailLoading(false);
      });
    return () => { cancelled = true; };
  }, [hoveredId, plantUuid, rows]);

  const devices = Array.from(new Map(rows.map((row) => [row.deviceId, row])).values());
  const tagClass = (tag: string | null) => tag?.includes('power') ? 'bg-amber-50 text-amber-700' : tag?.includes('voltage') ? 'bg-blue-50 text-blue-700' : 'bg-slate-100 text-slate-600';

  return (
    <div className="rounded-3xl border border-slate-200 bg-white shadow-sm">
      {collapsible ? <button type="button" onClick={() => setOpen((value) => !value)} className="flex w-full items-center justify-between px-5 py-4 text-left">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-slate-700">Smartlogger Verileri</div>
          <div className="mt-1 text-sm text-slate-500">Santral Smartlogger cihazından alınan anlık değerler</div>
        </div>
        {open ? <ChevronUp size={19} className="text-slate-400" /> : <ChevronDown size={19} className="text-slate-400" />}
      </button> : <h3 className="px-5 py-3 text-sm font-semibold text-slate-700">SmartLogger Verileri</h3>}
      {open && (
        <div className="border-t border-slate-100 px-5 pb-5 pt-4">
          {loading && <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">{Array.from({ length: 8 }).map((_, index) => <div key={index} className="h-28 animate-pulse rounded-2xl bg-slate-100" />)}</div>}
          {!loading && !error && rows.length === 0 && <div className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-5 text-sm text-slate-500">Smartlogger verisi bulunamadı.</div>}
          {!loading && !error && rows.length > 0 && (
            <>
              <div className="mb-4 overflow-x-auto rounded-2xl border border-slate-100 bg-slate-50/70">
                <div className="flex min-w-[980px] items-center gap-5 border-b border-slate-200 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <div className="w-40 shrink-0 flex items-center gap-2"><Radio size={15} className="text-blue-600" />Cihaz Tanımı</div><div className="w-36 shrink-0">Serial Number</div><div className="w-40 shrink-0">Name</div><div className="w-24 shrink-0">Brand</div><div className="w-28 shrink-0">Model</div><div className="w-52 shrink-0">Software Version</div><div className="w-24 shrink-0">Status</div><div className="min-w-64 flex-1">Web Base URL</div>
                </div>
                {devices.map((device) => (
                  <div key={device.deviceId} className="flex min-w-[980px] items-center gap-5 px-4 py-3 text-sm text-slate-800">
                    <div className="w-40 shrink-0 font-semibold text-slate-700">#{device.deviceId}</div><div className="w-36 shrink-0 truncate font-semibold">{device.serialNumber || '—'}</div><div className="w-40 shrink-0 truncate font-semibold">{device.deviceName || '—'}</div><div className="w-24 shrink-0 truncate">{device.brand || '—'}</div><div className="w-28 shrink-0 truncate">{device.model || '—'}</div><div className="w-52 shrink-0 truncate">{device.softwareVersion || '—'}</div>
                    <div className={`flex w-24 shrink-0 items-center gap-1 font-semibold ${device.status === 1 ? 'text-emerald-600' : 'text-rose-600'}`}>{device.status === 1 ? <Wifi size={14} /> : <WifiOff size={14} />}{device.status === 1 ? 'Aktif' : 'Pasif'}</div>
                    <div className="min-w-64 flex-1 truncate">{device.webBaseUrl ? <a href={device.webBaseUrl} target="_blank" rel="noreferrer" className="font-semibold text-blue-600 underline underline-offset-2 hover:text-blue-800">{device.webBaseUrl}</a> : '—'}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {rows.map((row) => (
                  <button type="button" key={`${row.deviceId}-${row.id}`} onClick={() => { setSelected(row); setHistoryDate(dateString(new Date())); setHistoryRows([]); setHistoryError(null); setHistoryOpen(true); }} onMouseEnter={() => setHoveredId(row.id)} onMouseLeave={() => setHoveredId(null)} className={`relative min-h-[112px] rounded-2xl border p-4 pr-36 text-left transition hover:-translate-y-0.5 hover:shadow-md ${selected?.id === row.id ? 'border-blue-300 bg-blue-50/60 shadow-sm' : 'border-slate-100 bg-slate-50/70'}`} title={row.notes || undefined}>
                    <div className="flex items-start justify-between gap-2"><div className="flex min-w-0 items-center gap-2 text-xs font-semibold text-slate-600"><span className="text-blue-600"><MetricIcon tag={row.webPageTag} /></span><span className="truncate">{row.quantity}</span></div><span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${tagClass(row.webPageTag)}`}>{row.webPageTag || 'Smartlogger'}</span></div>
                    <div className="mt-3 text-xl font-bold tabular-nums text-slate-900">{row.valueNumeric != null ? Number(row.valueNumeric).toLocaleString('tr-TR', { maximumFractionDigits: 2 }) : row.valueText || '—'}{row.valueNumeric != null && row.units ? <span className="ml-1 text-xs font-semibold text-slate-500">{row.units}</span> : null}</div>
                    <div className="mt-2 truncate text-[11px] text-slate-500">{row.dataType || 'Veri'}{row.notes ? ` · ${row.notes}` : ''}</div>
                    {hoveredId === row.id && <div className="absolute right-3 top-1/2 h-[76px] w-28 -translate-y-1/2 overflow-hidden rounded-xl border border-blue-100 bg-white">{thumbnailLoading && <div className="h-[76px] animate-pulse bg-slate-100" />}{!thumbnailLoading && thumbnailRows.length > 0 && <ThumbnailChart rows={thumbnailRows} />}{!thumbnailLoading && thumbnailRows.length === 0 && <div className="flex h-[76px] items-center justify-center text-[10px] text-slate-400">Bugün veri yok</div>}</div>}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
      {historyOpen && selected && <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm" onClick={() => setHistoryOpen(false)}><div className="w-full max-w-4xl rounded-3xl bg-white p-5 shadow-2xl" onClick={(event) => event.stopPropagation()}><div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4"><div><div className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Smartlogger Geçmiş Grafiği</div><div className="text-lg font-bold text-slate-900">{selected.quantity} · {selected.units || 'birimsiz'}</div><div className="text-xs text-slate-500">{selected.deviceName}</div></div><button type="button" onClick={() => setHistoryOpen(false)} className="rounded-full p-2 text-slate-400 hover:bg-slate-50 hover:text-slate-700"><X size={18} /></button></div><div className="mt-4 flex flex-wrap items-center justify-between gap-3"><div className="text-sm font-semibold text-slate-700">Günlük değer grafiği</div><div className="flex items-center gap-2"><button type="button" onClick={() => setHistoryDate(moveDate(historyDate, -1))} className="rounded-lg border border-blue-200 bg-white p-2 text-blue-700"><ChevronLeft size={16} /></button><input type="date" value={historyDate} onChange={(event) => setHistoryDate(event.target.value)} className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm text-slate-700" /><button type="button" onClick={() => setHistoryDate(moveDate(historyDate, 1))} className="rounded-lg border border-blue-200 bg-white p-2 text-blue-700"><ChevronRight size={16} /></button></div></div><div className="mt-4">{historyLoading && <div className="flex h-[340px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-500"><Loader2 size={18} className="mr-2 animate-spin" />Veriler yükleniyor...</div>}{!historyLoading && !historyError && historyRows.length === 0 && <div className="flex h-[340px] items-center justify-center rounded-2xl bg-slate-50 text-sm text-slate-500">Seçilen gün için kayıt bulunamadı.</div>}{!historyLoading && !historyError && historyRows.length > 0 && <HighchartsLine rows={historyRows} unit={selected.units} />}</div></div></div>}
    </div>
  );
}
